import {
  GridAggregationModel,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-premium'

export const defaultSortModel: GridSortModel = [{ field: 'name', sort: 'asc' }]
export const defaultFilterModel: GridFilterModel = {
  items: [],
  quickFilterValues: [],
}
export const MTB_EDITABLE_FIELDS = ['vertical', 'masterStatus', 'dateLaunched', 'startDate']
export const pinnedColumns = { left: ['__check__', 'name'], right: [] }

// 1) Gather every field name that ever appears in any view:
const allFields = [
  'name',
  'orgNameReference',
  'dateRange',
  'activeCount',
  'billingContact',
  'searchSpend',
  'metaSpend',
  'bingSpend',
  'spotifySpend',
  'linkedinSpend',
  'snapchatSpend',
  'tiktokSpend',
  'progDisplaySpend',
  'progAudioSpend',
  'displaySpend',
  'tier1Spend',
  'tier2Spend',
  'tier3Spend',
  'overallProgress',
  'amProgress',
  'seoProgress',
  'dmsProgress',
  'dmsOpts',
  'seoOpts',
  'totalQualifiedOpts',
  'opts',
  'seoStatus',
  'vertical',
  'masterStatus',
  'products',
  'seoGmb',
  'type',
  'timeCompleted',
  'seoTier',
  'dependenciesDueDate',
  'estLaunchDate',
  'dateLaunched',
  'startDate',
  'endDate',
  'contractedBy',
  'totalMediaSpend',
  'lastModified',
  'status',
  'implementationFee',
  'total',
  'team_details',
  'website',
  '__tree_data_group__',

  // There were a few keys that only showed up in certain views:
  'dmsOpts',
  'qualifyingTier',
  'amProgress',
  'dmsProgress',
  'seoProgress',
  'monthlySeo',
  'optsProgress',
  'activeCount',
  'slaDays',
  'owner',
  'creatives',
  'masterStatus',
  'team_details',
  // mtb
  'totalMediaSpend',
  'monthlyMediaSpend',
  'totalContractAmount',
  'monthlyContractAmount',
  'msaMonthlyTotal',
  'ioMonthlyTotal',
  'csaMonthlyTotal',
  'totalMonthlyFee',
  'totalQualifiedOpts',
  'month',
  'isGoogleDisplay',
  'isGoogleSearch',
  'isMetaAdvertising',
  'isMicrosoftAdvertising',
  'isSimplifi',
  'isSnapchat',
  'isSpotify',
  'isTikTok',
  'isYoutube',
  'seoTier',
  'isSeo1',
  'isSeo2',
  'isSeo3',
  'isSeo4',
]

// 2) Build a “base” object where everything is false:
const baseVisibility: Record<string, boolean> = {}
allFields.forEach(fieldName => {
  baseVisibility[fieldName] = false
})

// 3) Now define each view by spreading the base and overriding only the “true” bits
//    (and any forced‐false bits, if necessary).

export const defaultVisibilityModel: GridColumnVisibilityModel = {
  ...baseVisibility,
  name: true,
  activeCount: true,
  overallProgress: true,
  amProgress: true,
  seoProgress: true,
  dmsProgress: true,
  masterStatus: true,
  team_details: true,
  totalContractAmount: true,
  monthlyContractAmount: true,
}

export const spendViewState = {
  ...baseVisibility,
  name: true,
  activeCount: true,
  searchSpend: true,
  metaSpend: true,
  bingSpend: true,
  spotifySpend: true,
  linkedinSpend: true,
  snapchatSpend: true,
  tiktokSpend: true,
  progDisplaySpend: true,
  progAudioSpend: true,
  displaySpend: true,
  tier1Spend: true,
  tier2Spend: true,
  tier3Spend: true,
  totalMediaSpend: true,
}

export const amViewState = {
  ...baseVisibility,
  name: true,
  activeCount: true,
  contractedBy: true,
  billingContact: true,
  overallProgress: true,
  amProgress: true,
  dmsProgress: true,
  masterStatus: true,
  team_details: true,
}

export const dmsViewState = {
  ...baseVisibility,
  name: true,
  activeCount: true,
  contractedBy: true,
  billingContact: true,
  overallProgress: true,
  dmsProgress: true,
  dmsOpts: true,
  opts: true,
  masterStatus: true,
  team_details: true,
}
export const monthlyTotalViewState = {
  ...baseVisibility,
  name: true,
  activeCount: true,
  contractedBy: true,
  billingContact: true,
  masterStatus: true,
  team_details: true,
  msaMonthlyTotal: true,
  ioMonthlyTotal: true,
  csaMonthlyTotal: true,
  totalMonthlyFee: true,
  monthlyContractAmount: true,
  monthlyMediaSpend: true,
}
export const aggregationModel: GridAggregationModel = {
  totalContractAmount: 'sum',
  monthlyContractAmount: 'sum',
  // overallProgress: 'sum',
  // activeCount: 'sum',
  searchSpend: 'sum',
  metaSpend: 'sum',
  bingSpend: 'sum',
  spotifySpend: 'sum',
  linkedinSpend: 'sum',
  snapchatSpend: 'sum',
  tiktokSpend: 'sum',
  progDisplaySpend: 'sum',
  progAudioSpend: 'sum',
  displaySpend: 'sum',
  dmsOpts: 'sum',
  seoOpts: 'sum',
  totalQualifiedOpts: 'sum',
  opts: 'sum',
  totalMediaSpend: 'sum',
  monthlyMediaSpend: 'sum',
  msaMonthlyTotal: 'sum',
  ioMonthlyTotal: 'sum',
  csaMonthlyTotal: 'sum',
  totalMonthlyFee: 'sum',
  implementationFee: 'sum',
  grossProfit: 'sum',
  total: 'sum',
}

export const invoiceViewState: GridColumnVisibilityModel = {
  id: false,
  implementationFee: false,
  total: false,
  dealStatus: false,
  reasonChurn: false,
  lastDateOfService: false,
  churnNote: false,
  organization: false,
  type: false,
  owner: false,
  billingStatus: false,
  billingContact: false,
  primaryContact: false,
  lastModified: false,
  dba: false,
}
export const churnViewState: GridColumnVisibilityModel = {
  id: false,
  implementationFee: false,
  total: false,
  dealStage: false,
  invoiceStatus: false,
  organization: false,
  type: false,
  startDate: false,
  endDate: false,
  owner: false,
  billingStatus: false,
  billingContact: false,
  primaryContact: false,
  lastModified: false,
  dba: false,
}
export const billingViewState: GridColumnVisibilityModel = {
  id: false,
  implementationFee: false,
  total: false,
  dealStatus: false,
  invoiceStatus: false,
  reasonChurn: false,
  lastDateOfService: false,
  churnNote: false,
  organization: false,
  type: false,
  owner: false,
  lastModified: false,
  dba: false,
}

export const defaultColumnOrder: string[] = [
  'name',
  'orgNameReference',
  'commentCount',
  'activeCount',
  'searchSpend',
  'metaSpend',
  'bingSpend',
  'spotifySpend',
  'linkedinSpend',
  'snapchatSpend',
  'tiktokSpend',
  'progDisplaySpend',
  'progAudioSpend',
  'displaySpend',
  'overallProgress',
  'amProgress',
  'seoProgress',
  'dmsProgress',
  'dmsOpts',
  'seoOpts',
  'totalQualifiedOpts',
  'opts',
  'onboardingStatus',
  'vertical',
  'masterStatus',
  'type',
  'timeCompleted',
  'seoTier',
  'dependenciesDueDate',
  'estLaunchDate',
  'dateLaunched',
  'startDate',
  'dateRange',
  'endDate',
  'contractedBy',
  'totalMediaSpend',
  'monthlyMediaSpend',
  'totalContractAmount',
  'msaMonthlyTotal',
  'ioMonthlyTotal',
  'csaMonthlyTotal',
  'totalMonthlyFee',
  'month',
  'lastModified',
  'status',
  'website',
  'implementationFee',
  'total',
  'grossProfit',
  'team_details',
]
